// core
import React from "react";
import { useFormikContext } from "formik";
import Dropdown from "../../../../common/components/Forms/Dropdown/Dropdown";
import MonthlyCoverSlider from "../../../../common/components/Forms/MonthlyCoverSlider/MonthlyCoverSlider";
import { IClientSettingsBoxProduct } from "../ClientSettingsBox/ClientSettingsBox";
import ToolTip from "../../../../common/components/Tooltips/Tooltip";
import { PaymentPeriod } from "../../../../common/interfaces/enums/PaymentPeriod";
import ClientSettingsBoxOccupation from "../ClientSettingsBoxOccupation/ClientSettingsBoxOccupation";
import { IQuoteSettings } from "../../../interfaces/form/IQuoteSettings";
import ProductType from "../../../../common/interfaces/enums/ProductType";
import useSettingsValidation from "../../../hooks/useSettingsValidation";
import {
    maxMonthlyIncomeCoverPercentage,
    calculateMaxIncomeMonthlyCover
} from "../../../../common/shared/productHelpers";

const ClientSettingsBoxIncome: React.FC<IClientSettingsBoxProduct> = (props) => {
    const { client, name, setClient } = props;
    const { handleChange, handleBlur } = useFormikContext<IQuoteSettings>();
    const { getFormikError } = useSettingsValidation();

    const waitPeriodOptions = [
        { value: "4", label: "4 Weeks" },
        { value: "8", label: "8 Weeks" },
        { value: "13", label: "13 Weeks" }
    ];

    const paymentPeriodOptions = [
        { value: PaymentPeriod.TwoYears, label: "Two Years" },
        { value: PaymentPeriod.FiveYears, label: "Five Years" },
        { value: PaymentPeriod.UpToAgeSixtyFive, label: "To Age 65" },
        { value: PaymentPeriod.UpToAgeSeventy, label: "To Age 70" }
    ];

    return (
        <React.Fragment>
            <ClientSettingsBoxOccupation
                client={client}
                name={name}
                setClient={setClient}
            />

            <MonthlyCoverSlider
                id={`${name}.${ProductType.Income}.monthlyCover`}
                name={`${name}.${ProductType.Income}.monthlyCover`}
                label={"Monthly Income Cover"}
                maxValue={calculateMaxIncomeMonthlyCover(client.occupation?.income)}
                value={client[ProductType.Income]?.monthlyCover}
                onChange={(monthlyCover) => {
                    setClient({
                        ...client,
                        [ProductType.Income]: {
                            ...client[ProductType.Income],
                            monthlyCover
                        }
                    });
                }}
                onBlur={handleBlur}
                isDisabled={!client.income || !client.occupation?.income}
                percentageRangeMax={maxMonthlyIncomeCoverPercentage}
                hasInput
                isCurrency
                isRoundUp
                tooltipContent="You can insure up to 75% of your gross (before tax) income. Most people insure 75% but you can protect less if you want to."
                hasError={
                    getFormikError(`${name}.${ProductType.Income}.monthlyCover`) ||
                    getFormikError(`${name}.${ProductType.Income}`)
                }
            />

            <Dropdown
                id={`${name}.${ProductType.Income}.waitPeriodWeeks`}
                name={`${name}.${ProductType.Income}.waitPeriodWeeks`}
                title={`Wait Period`}
                selectedOptionId={client[ProductType.Income]?.waitPeriodWeeks.toString()}
                options={waitPeriodOptions}
                onChange={(e) =>
                    setClient({
                        ...client,
                        [ProductType.Income]: {
                            ...client[ProductType.Income],
                            waitPeriodWeeks: parseInt(e.target.value)
                        }
                    })
                }
                onBlur={handleBlur}
                tooltip={
                    <ToolTip tooltipContent="Your claim payments start after your Wait Period. A longer wait period lowers the costs, but you need to be confident you can get by (e.g. using savings or sick leave)." />
                }
                hasError={getFormikError(`${name}.${ProductType.Income}.waitPeriodWeeks`)}
            />

            <Dropdown
                id={`${name}.${ProductType.Income}.paymentPeriod`}
                name={`${name}.${ProductType.Income}.paymentPeriod`}
                title={`Payment Period`}
                selectedOptionId={client[ProductType.Income]?.paymentPeriod.toString()}
                options={paymentPeriodOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                noMargin
                tooltip={
                    <ToolTip
                        tooltipContent={
                            "Your claim payments continue for the Payment Period you choose. A longer Payment Period costs more but gives you better longer term protection."
                        }
                    />
                }
                hasError={getFormikError(`${name}.${ProductType.Income}.paymentPeriod`)}
            />
        </React.Fragment>
    );
};

export default ClientSettingsBoxIncome;
